import type { HRContractTemplate, HREmployee, HREmployeeContract } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Download, Power, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const terminateSchema = z.object({
  reason: z.string().optional(),
})

type TerminateFormValues = z.infer<typeof terminateSchema>

export const Route = createFileRoute('/hr/contracts/$contractId')({
  component: ContractDetailPage,
})

function ContractDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <ContractDetail />
    </ModuleGuard>
  )
}

function ContractDetail() {
  const params = Route.useParams()
  const contractId = Number(params.contractId)
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false)

  const queryClient = useQueryClient()

  const {
    data: contract,
    isLoading,
    refetch,
  } = useQuery(orpc.hr.getContract.queryOptions({ input: { id: contractId } }))

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const employees = (employeesData?.data ?? []) as HREmployee[]

  const data = contract as
    | (HREmployeeContract & {
        employee?: HREmployee
        template?: HRContractTemplate
      })
    | null
    | undefined

  const activateMutation = useMutation(
    orpc.hr.activateContract.mutationOptions({
      onSuccess: () => {
        toast.success('Contract activated')
        refetch()
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContracts']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const terminateMutation = useMutation(
    orpc.hr.terminateContract.mutationOptions({
      onSuccess: () => {
        toast.success('Contract terminated')
        refetch()
        queryClient.invalidateQueries({ queryKey: [['hr', 'listContracts']] })
        setTerminateDialogOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const downloadUrlQuery = useQuery({
    ...orpc.hr.getContractDownloadUrl.queryOptions({ input: { id: contractId } }),
    enabled: false,
  })

  const handleDownload = async () => {
    const result = await downloadUrlQuery.refetch()
    if (result.data?.url) {
      window.open(result.data.url, '_blank')
    }
  }

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Contract not found</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/hr/contracts">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contracts
          </Link>
        </Button>
      </div>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{data.title}</h1>
          <p className="text-muted-foreground">{getEmployeeName(data.employeeId)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={data.status} />
          {data.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => activateMutation.mutate({ id: contractId })}
              disabled={activateMutation.isPending}
            >
              <Power className="h-4 w-4 mr-2" />
              {activateMutation.isPending ? 'Activating...' : 'Activate'}
            </Button>
          )}
          {data.status === 'active' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setTerminateDialogOpen(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Terminate
            </Button>
          )}
          {data.fileUrl && (
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Employee</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{getEmployeeName(data.employeeId)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Start Date</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {data.startDate ? new Date(data.startDate).toLocaleDateString() : '-'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">End Date</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {data.endDate ? new Date(data.endDate).toLocaleDateString() : 'Open-ended'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Template</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{data.template?.name ?? 'None'}</p>
          </CardContent>
        </Card>

        {data.signedAt && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Signed At</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{new Date(data.signedAt).toLocaleDateString()}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {data.notes && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{data.notes}</p>
          </CardContent>
        </Card>
      )}

      {data.generatedContent && (
        <Card>
          <CardHeader>
            <CardTitle>Contract Content</CardTitle>
            <CardDescription>Generated from template</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-sm max-w-none"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: data.generatedContent }}
            />
          </CardContent>
        </Card>
      )}

      <TerminateDialog
        open={terminateDialogOpen}
        onOpenChange={setTerminateDialogOpen}
        isPending={terminateMutation.isPending}
        onSubmit={(reason) => terminateMutation.mutate({ id: contractId, reason })}
      />
    </div>
  )
}

interface TerminateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (reason?: string) => void
}

function TerminateDialog({ open, onOpenChange, isPending, onSubmit }: TerminateDialogProps) {
  const form = useForm<TerminateFormValues>({
    resolver: zodResolver(terminateSchema),
    defaultValues: { reason: '' },
  })

  const handleSubmit = (data: TerminateFormValues) => {
    onSubmit(data.reason || undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Terminate Contract</DialogTitle>
          <DialogDescription>
            Are you sure you want to terminate this contract? Optionally provide a reason.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason (optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Reason for termination..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending ? 'Terminating...' : 'Terminate Contract'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
